/**
 * Touch Gestures Hook
 * 
 * Manages touch event state and handlers for swipe-down gesture detection
 * on bottom drawers.
 */

import * as React from "react";
import type { DrawerConfig } from "../types.js";
import {
  calculateVelocity,
  shouldCloseOnSwipe,
  calculateSwipeOffset,
  type TouchData,
} from "../utils/touchGestures.js";

/**
 * Touch gesture state
 */
export interface TouchGestureState {
  touchStart: TouchData | null;
  touchCurrent: { y: number } | null;
}

/**
 * Hook for managing touch gesture state and handlers
 * 
 * @param finalConfig - Drawer configuration
 * @param onClose - Callback when drawer should close
 * @returns Object with touch state and event handlers
 */
export function useTouchGestures(
  finalConfig: DrawerConfig,
  onClose: () => void
) {
  const [touchStart, setTouchStart] = React.useState<TouchData | null>(null);
  const [touchCurrent, setTouchCurrent] = React.useState<{ y: number } | null>(null);

  const handleTouchStart = React.useCallback(
    (e: React.TouchEvent) => {
      if (!finalConfig.enableSwipeGesture || finalConfig.side !== "bottom") return;

      const touch = e.touches[0];
      setTouchStart({ y: touch.clientY, time: Date.now() });
      setTouchCurrent({ y: touch.clientY });
    },
    [finalConfig.enableSwipeGesture, finalConfig.side]
  );

  const handleTouchMove = React.useCallback(
    (e: React.TouchEvent) => {
      if (!finalConfig.enableSwipeGesture || finalConfig.side !== "bottom" || !touchStart) return;

      const touch = e.touches[0];
      setTouchCurrent({ y: touch.clientY });
    },
    [finalConfig.enableSwipeGesture, finalConfig.side, touchStart]
  );

  const handleTouchEnd = React.useCallback(
    (e: React.TouchEvent) => {
      if (!finalConfig.enableSwipeGesture || finalConfig.side !== "bottom" || !touchStart || !touchCurrent) {
        return;
      }

      const touch = e.changedTouches[0];
      const deltaY = touch.clientY - touchStart.y;
      const deltaTime = Date.now() - touchStart.time;
      const velocity = calculateVelocity(deltaY, deltaTime);

      if (shouldCloseOnSwipe(deltaY, velocity)) {
        onClose();
      }

      setTouchStart(null);
      setTouchCurrent(null);
    },
    [finalConfig.enableSwipeGesture, finalConfig.side, touchStart, touchCurrent, onClose]
  );

  const swipeOffset = React.useMemo(() => {
    if (touchStart && touchCurrent) {
      return calculateSwipeOffset(touchCurrent.y, touchStart.y);
    }
    return 0;
  }, [touchStart, touchCurrent]);

  return React.useMemo(
    () => ({
      touchStart,
      touchCurrent,
      swipeOffset,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
    }),
    [touchStart, touchCurrent, swipeOffset, handleTouchStart, handleTouchMove, handleTouchEnd]
  );
}
